import { Box } from '@mui/material';
import { Handle, Position, useReactFlow } from '@xyflow/react';

import { isDefined } from 'common';

import {
  EdgeHandleTypeToColor,
  NODE_HANDLE_POSITION_STYLES,
  type NodeHandleProps,
} from './constants';
import {
  EventAction,
  EventCategory,
  useCaptureEvents,
} from '../../../../../../../../utils/events/index';
import { useFlowDefinitionContext } from '../../../../../context';
import { type ReactFlowEdge, type ReactFlowNode } from '../../../../../types';
import { getFlowDefinitionEventProperties } from '../../../../../utils';
import { useReactFlowViewContext } from '../../../../context/context';
import { NODE_WIDTH_IN_PIXELS } from '../cardDimensions';

const NODE_HANDLE_SIZE_IN_PIXELS = 14;
const NODE_SVG_SIZE_IN_PIXELS = NODE_HANDLE_SIZE_IN_PIXELS - 2;
const NODE_RADIUS_IN_PIXELS = NODE_SVG_SIZE_IN_PIXELS / 2;

function getNewNodePosition(handlePosition: Position, handleCenter: { x: number; y: number }) {
  switch (handlePosition) {
    case Position.Top:
      return {
        x: handleCenter.x - NODE_WIDTH_IN_PIXELS / 2,
        y: handleCenter.y - 4 * 16,
      };
    case Position.Left:
      return {
        x: handleCenter.x - 4 * 16,
        y: handleCenter.y,
      };
    case Position.Right:
      return {
        x: handleCenter.x + 4 * 16,
        y: handleCenter.y,
      };
    case Position.Bottom:
      return {
        x: handleCenter.x - NODE_WIDTH_IN_PIXELS / 2,
        y: handleCenter.y + 4 * 16,
      };
    default:
      // eslint-disable-next-line @typescript-eslint/restrict-template-expressions
      throw new Error(`Invalid handle position: ${handlePosition}`);
  }
}

export function EditableHandle(props: NodeHandleProps) {
  const { style, nodeProps, edgeHandleType, ...handleProps } = props;
  const { position } = handleProps;
  const { reactFlowMutators, setSelectedNodeId } = useReactFlowViewContext();
  const { screenToFlowPosition, getNode } = useReactFlow<ReactFlowNode, ReactFlowEdge>();
  const { captureEvent } = useCaptureEvents();
  const { flowDefinition } = useFlowDefinitionContext();

  return (
    <Handle
      {...handleProps}
      style={{
        width: NODE_HANDLE_SIZE_IN_PIXELS,
        height: NODE_HANDLE_SIZE_IN_PIXELS,
        backgroundColor: 'white',
        backgroundRepeat: 'no-repeat',
        cursor: 'pointer',
        overflow: 'unset',
        ...NODE_HANDLE_POSITION_STYLES[position],
        ...style,
      }}
      role="button"
      aria-label="Add node"
      tabIndex={0}
      onClick={
        reactFlowMutators
          ? (event) => {
              event.stopPropagation();
              const buttonBoundingRect = event.currentTarget.getBoundingClientRect();
              const handleCenter = screenToFlowPosition({
                x: buttonBoundingRect.left + buttonBoundingRect.width / 2,
                y: buttonBoundingRect.top + buttonBoundingRect.height / 2,
              });
              let positionOffset = { x: 0, y: 0 };
              const { parentId } = nodeProps;
              if (isDefined(parentId)) {
                const parentNode = getNode(parentId);
                if (isDefined(parentNode)) {
                  positionOffset = {
                    x: parentNode.position.x,
                    y: parentNode.position.y,
                  };
                }
              }
              const newNodePosition = getNewNodePosition(position, handleCenter);
              const newNode = reactFlowMutators.addPlaceholderNode({
                connectingNode: {
                  nodeId: nodeProps.id,
                  // handleId is undefined for default handles, or it is the condition or error handle name.
                  handleId: handleProps.id,
                  handleType: handleProps.type,
                },
                parentId,
                position: {
                  x: newNodePosition.x - positionOffset.x,
                  y: newNodePosition.y - positionOffset.y,
                },
              });
              setSelectedNodeId(newNode.id);
              captureEvent({
                category: EventCategory.FlowDefinitionEdit,
                action: EventAction.BlockCreateButtonClick,
                properties: getFlowDefinitionEventProperties(flowDefinition),
              });
            }
          : undefined
      }
    >
      <Box
        sx={{
          position: 'absolute',
          top: -1,
          left: -1,
          width: `${NODE_HANDLE_SIZE_IN_PIXELS}px`,
          height: `${NODE_HANDLE_SIZE_IN_PIXELS}px`,
          stroke: EdgeHandleTypeToColor[edgeHandleType],
          fill: EdgeHandleTypeToColor[edgeHandleType],
          transition: 'all 0.25s ease-in-out',
          /**
           * Important - allow pointer-events to propagate to the parent Handle component.
           * This allows edge dragging.
           */
          pointerEvents: 'none',
        }}
      >
        <svg
          xmlns="http://www.w3.org/2000/svg"
          width={`${NODE_HANDLE_SIZE_IN_PIXELS}px`}
          height={`${NODE_HANDLE_SIZE_IN_PIXELS}px`}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
          }}
        >
          <circle
            cx={NODE_RADIUS_IN_PIXELS + 1}
            cy={NODE_RADIUS_IN_PIXELS + 1}
            r={NODE_RADIUS_IN_PIXELS}
          />
          <line
            x1={NODE_RADIUS_IN_PIXELS + 1}
            y1="1"
            x2={NODE_RADIUS_IN_PIXELS + 1}
            y2={NODE_SVG_SIZE_IN_PIXELS + 1}
            stroke="white"
            strokeWidth="2"
          />
          <line
            x1="1"
            y1={NODE_RADIUS_IN_PIXELS + 1}
            x2={NODE_SVG_SIZE_IN_PIXELS + 1}
            y2={NODE_RADIUS_IN_PIXELS + 1}
            stroke="white"
            strokeWidth="2"
          />
          <circle
            cx={NODE_RADIUS_IN_PIXELS + 1}
            cy={NODE_RADIUS_IN_PIXELS + 1}
            r={NODE_RADIUS_IN_PIXELS}
            strokeWidth="2"
            fill="transparent"
          />
        </svg>
      </Box>
    </Handle>
  );
}
